/**
 * Convert a HEIC/HEIF photo to JPEG in the browser, before upload (#16099).
 *
 * iPhone photos are HEIC. The server accepts only JPEG, PNG and WebP images,
 * and its `sharp` binary has no HEVC decoder, so every upload entry point
 * passes a chosen file through {@link normalizeImageFile} first: anything that
 * is not HEIC comes back as the same object, and a HEIC comes back as a JPEG
 * named `<base>.jpg`. The server only ever sees the JPEG.
 *
 * ## The decoder
 *
 * `heic-to` is libheif compiled to WASM (LGPL-3.0). It is about 3 MB, so it is
 * loaded with a dynamic `import()` only when a HEIC is actually chosen — its
 * own chunk, never in the main bundle, and never fetched for any other file.
 * The `/csp` build is the one used: the default build instantiates through
 * `new Function`, which the app's CSP (`script-src` without `'unsafe-eval'`,
 * see `src/proxy.ts`) refuses. The CSP build decodes in a `blob:` worker,
 * which `worker-src 'self' blob:` and `'wasm-unsafe-eval'` allow.
 *
 * libheif applies the container's rotation/mirror transforms (`irot`/`imir`,
 * which is where an iPhone records orientation), so the JPEG is upright.
 * Only the primary image is decoded; a Live Photo / sequence keeps its first.
 *
 * A HEIC is far smaller than the same photo as a JPEG, so a photo picked well
 * under the upload limit can come out over it. Rather than refuse it, the
 * decode is retried at lower qualities; a JPEG still over the limit after the
 * last one is returned as-is and the caller's size check reports it.
 *
 * The decoder loader is a parameter so the contract is testable without a
 * browser; the real decode is covered by the golden-path e2e.
 */

import { getMaxSizeForType } from '@/lib/storage/file-types'

import {
  HEIC_CONVERSION_FAILED_MESSAGE,
  HEIC_CONVERTER_UNAVAILABLE_MESSAGE,
  isHeicFile,
} from './heic'

/**
 * JPEG qualities tried in order, stopping at the first output within the size
 * limit. The first is the server's own value (85).
 */
export const HEIC_JPEG_QUALITIES: readonly number[] = [0.85, 0.7, 0.5]

const JPEG_MIME_TYPE = 'image/jpeg'

/** Decodes a HEIC blob and resolves with a JPEG blob at `quality` (0–1). */
export type HeicDecoder = (blob: Blob, quality: number) => Promise<Blob>

export interface NormalizeImageFileOptions {
  /** Loads the decoder. Defaults to the lazily imported `heic-to`. */
  loadDecoder?: () => Promise<HeicDecoder>
  /**
   * The upload size limit the JPEG should fit — pass the caller's own when it
   * has one. Defaults to the allowlist's limit for a JPEG.
   */
  maxSizeBytes?: number
}

/** A HEIC that could not be converted; `message` is safe to show. */
export class HeicConversionError extends Error {
  readonly cause: unknown

  constructor(options?: { cause?: unknown; message?: string }) {
    super(options?.message ?? HEIC_CONVERSION_FAILED_MESSAGE)
    this.name = 'HeicConversionError'
    this.cause = options?.cause
  }
}

async function loadHeicTo(): Promise<HeicDecoder> {
  const { heicTo } = await import('heic-to/csp')
  return (blob, quality) => heicTo({ blob, type: JPEG_MIME_TYPE, quality })
}

/** `IMG_0042.HEIC` → `IMG_0042.jpg`; a name without one gains `.jpg`. */
function jpegName(name: string): string {
  const base = name.replace(/\.(heic|heif)$/i, '')
  return `${base || 'photo'}.jpg`
}

/**
 * The file to upload: `file` itself unless it is HEIC, else a JPEG copy.
 *
 * @throws {HeicConversionError} when a HEIC cannot be decoded.
 */
export async function normalizeImageFile(
  file: File,
  {
    loadDecoder = loadHeicTo,
    maxSizeBytes = getMaxSizeForType(JPEG_MIME_TYPE),
  }: NormalizeImageFileOptions = {},
): Promise<File> {
  if (!isHeicFile(file)) return file

  let decode: HeicDecoder
  try {
    decode = await loadDecoder()
  } catch (cause) {
    throw new HeicConversionError({
      cause,
      message: HEIC_CONVERTER_UNAVAILABLE_MESSAGE,
    })
  }

  let jpeg: Blob | null = null
  for (const quality of HEIC_JPEG_QUALITIES) {
    try {
      jpeg = await decode(file, quality)
    } catch (cause) {
      throw new HeicConversionError({ cause })
    }
    if (jpeg.size <= 0) throw new HeicConversionError()
    if (jpeg.size <= maxSizeBytes) break
  }
  if (jpeg === null) throw new HeicConversionError()

  return new File([jpeg], jpegName(file.name), {
    type: JPEG_MIME_TYPE,
    lastModified: file.lastModified,
  })
}
